import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";

export interface BulkActionResult {
  success: boolean;
  message: string;
  updated: number;
  total: number;
  errors?: string[];
}

interface BulkActionDialogsProps {
  // Category dialog
  showCategoryDialog: boolean;
  onCategoryDialogOpenChange: (open: boolean) => void;
  categoryValue: string;
  onCategoryChange: (value: string) => void;
  onCategorySubmit: () => Promise<void>;
  isCategoryLoading: boolean;

  // Tier dialog
  showTierDialog: boolean;
  onTierDialogOpenChange: (open: boolean) => void;
  tierValue: string;
  onTierChange: (value: string) => void;
  onTierSubmit: () => Promise<void>;
  isTierLoading: boolean;

  // Image dialog
  showImageDialog: boolean;
  onImageDialogOpenChange: (open: boolean) => void;
  imageUrl: string;
  onImageUrlChange: (value: string) => void;
  onImageSubmit: () => Promise<void>;
  isImageLoading: boolean;

  // Result display
  result: BulkActionResult | null;
  onResultClose: () => void;

  selectedCount: number;
}

export function BulkActionDialogs({
  showCategoryDialog,
  onCategoryDialogOpenChange,
  categoryValue,
  onCategoryChange,
  onCategorySubmit,
  isCategoryLoading,
  showTierDialog,
  onTierDialogOpenChange,
  tierValue,
  onTierChange,
  onTierSubmit,
  isTierLoading,
  showImageDialog,
  onImageDialogOpenChange,
  imageUrl,
  onImageUrlChange,
  onImageSubmit,
  isImageLoading,
  result,
  onResultClose,
  selectedCount,
}: BulkActionDialogsProps) {
  return (
    <>
      {/* Bulk Category Update Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={onCategoryDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Update Category</DialogTitle>
            <DialogDescription>
              Update category for {selectedCount} selected game{selectedCount !== 1 ? "s" : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Enter new category"
              value={categoryValue}
              onChange={(e) => onCategoryChange(e.target.value)}
              disabled={isCategoryLoading}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onCategoryDialogOpenChange(false)} disabled={isCategoryLoading}>
              Cancel
            </Button>
            <Button onClick={onCategorySubmit} disabled={isCategoryLoading || !categoryValue.trim()}>
              {isCategoryLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Tier Update Dialog */}
      <Dialog open={showTierDialog} onOpenChange={onTierDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Update Tier</DialogTitle>
            <DialogDescription>
              Update tier for {selectedCount} selected game{selectedCount !== 1 ? "s" : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={tierValue} onValueChange={onTierChange} disabled={isTierLoading}>
              <SelectTrigger>
                <SelectValue placeholder="Select tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="S">S Tier</SelectItem>
                <SelectItem value="A">A Tier</SelectItem>
                <SelectItem value="B">B Tier</SelectItem>
                <SelectItem value="C">C Tier</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onTierDialogOpenChange(false)} disabled={isTierLoading}>
              Cancel
            </Button>
            <Button onClick={onTierSubmit} disabled={isTierLoading || !tierValue}>
              {isTierLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Image Replace Dialog */}
      <Dialog open={showImageDialog} onOpenChange={onImageDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Replace Images</DialogTitle>
            <DialogDescription>
              Replace images for {selectedCount} selected game{selectedCount !== 1 ? "s" : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Enter image URL"
              value={imageUrl}
              onChange={(e) => onImageUrlChange(e.target.value)}
              disabled={isImageLoading}
              type="url"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onImageDialogOpenChange(false)} disabled={isImageLoading}>
              Cancel
            </Button>
            <Button onClick={onImageSubmit} disabled={isImageLoading || !imageUrl.trim()}>
              {isImageLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Replace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result Display Dialog */}
      {result && (
        <Dialog open={!!result} onOpenChange={onResultClose}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {result.success ? (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    Success
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    Completed with Errors
                  </>
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Alert>
                <AlertDescription>{result.message}</AlertDescription>
              </Alert>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Updated</p>
                  <p className="text-lg font-semibold">{result.updated}</p>
                </div>
                <div>
                  <p className="text-gray-600">Total</p>
                  <p className="text-lg font-semibold">{result.total}</p>
                </div>
              </div>
              {result.errors && result.errors.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-red-600">Errors:</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {result.errors.map((error, idx) => (
                      <p key={idx} className="text-xs text-red-500">
                        • {error}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={onResultClose}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
